import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import {
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Tooltip,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { AiOutlineVerticalAlignBottom } from 'react-icons/ai';
import { toast } from 'react-toastify';
import { AddCommasToAmount } from '../../helper/Utils';
import { extractDocumentServePath, openSecureDocumentOrToast } from '../../helper/secureDocuments';

const attachmentValue = (challan) => {
    const stored = String(challan?.challan_url || challan?.document_ref || '').trim();
    if (!stored || stored === '0' || stored.toLowerCase() === 'null') return '';
    return stored;
};

const formatDate = (value) => {
    if (!value) return '-';
    const parsed = moment(value, ['YYYY-MM-DD', 'DD-MM-YYYY', 'YYYY-MM-DD HH:mm:ss'], true);
    return parsed.isValid() ? parsed.format('DD-MM-YYYY') : value;
};

const formatMoney = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    return AddCommasToAmount(value) || '-';
};

const sumField = (invoices, field) => invoices.reduce(
    (sum, invoice) => sum + (Number(invoice?.[field]) || 0),
    0
);

const isPresentId = (value) => {
    if (value == null) return false;
    const text = String(value).trim();
    return text !== '' && text !== '0' && text.toLowerCase() !== 'null';
};

const resolveGstChallanId = (challan) => {
    const explicit = challan?.challan_id ?? challan?.gst_challan_id;
    if (isPresentId(explicit)) return String(explicit).trim();
    return '';
};

const challanDocumentRef = (challan) => {
    const servePath = extractDocumentServePath(attachmentValue(challan));
    if (servePath) return servePath;
    const id = resolveGstChallanId(challan);
    if (!id) return '';
    return `documents/serve/gst_challan/${id}`;
};

const hasChallanAttachment = (challan) => Boolean(challanDocumentRef(challan) || attachmentValue(challan));

const toChallan = (row, invoices, index) => ({
    key: String(row?.challan_id || row?.gst_challan_id || row?.challan_no || index),
    challan_id: row?.challan_id || row?.gst_challan_id || '',
    challan_no: row?.challan_no || '',
    challan_date: row?.challan_date || '',
    cheque_no: row?.cheque_no || '',
    cheque_date: row?.cheque_date || '',
    challan_url: row?.challan_url || row?.document_ref || '',
    i_gst_amount: row?.i_gst_amount,
    c_gst_amount: row?.c_gst_amount,
    s_gst_amount: row?.s_gst_amount,
    challan_value: row?.challan_value,
    invoices,
});

const challanGroupKey = (row, index) => {
    const challanNo = String(row?.challan_no || '').trim();
    if (challanNo) return `no:${challanNo}|${row?.challan_date || ''}`;
    return `row:${row?.invoice_id || row?.id || index}`;
};

const toMoney = (value) => {
    const amount = Number(value);
    return Number.isNaN(amount) ? '0.00' : amount.toFixed(2);
};

const withTotals = (group, preferServerTotals = false) => {
    const invoices = group.invoices || [];
    if (preferServerTotals) {
        const igst = Number(group.i_gst_amount) || 0;
        const cgst = Number(group.c_gst_amount) || 0;
        const sgst = Number(group.s_gst_amount) || 0;
        const challanValue = group.challan_value !== null && group.challan_value !== undefined && group.challan_value !== ''
            ? Number(group.challan_value)
            : igst + cgst + sgst;
        return {
            ...group,
            i_gst_amount: toMoney(igst),
            c_gst_amount: toMoney(cgst),
            s_gst_amount: toMoney(sgst),
            challan_value: toMoney(challanValue),
        };
    }
    const igst = invoices.length ? sumField(invoices, 'i_gst_amount') : Number(group.i_gst_amount) || 0;
    const cgst = invoices.length ? sumField(invoices, 'c_gst_amount') : Number(group.c_gst_amount) || 0;
    const sgst = invoices.length ? sumField(invoices, 's_gst_amount') : Number(group.s_gst_amount) || 0;
    return {
        ...group,
        i_gst_amount: igst.toFixed(2),
        c_gst_amount: cgst.toFixed(2),
        s_gst_amount: sgst.toFixed(2),
        challan_value: (igst + cgst + sgst).toFixed(2),
    };
};

const groupFlatInvoices = (rows) => {
    const groups = new Map();
    rows.forEach((row, index) => {
        const challanId = row?.challan_id || row?.gst_challan_id || '';
        const key = challanGroupKey(row, index);
        if (!groups.has(key)) {
            groups.set(key, toChallan(row, [], index));
            groups.get(key).key = key;
            groups.get(key).challan_id = challanId || '';
        }
        const group = groups.get(key);
        if (!group.cheque_no && row?.cheque_no) group.cheque_no = row.cheque_no;
        if (!group.cheque_date && row?.cheque_date) group.cheque_date = row.cheque_date;
        if (!group.challan_url && (row?.challan_url || row?.document_ref)) {
            group.challan_url = row.challan_url || row.document_ref;
        }
        if (!group.challan_id && challanId) group.challan_id = challanId;
        const invoiceId = row?.invoice_id || row?.id;
        const alreadyAdded = invoiceId != null && group.invoices.some(
            (item) => String(item?.invoice_id || item?.id) === String(invoiceId)
        );
        if (!alreadyAdded) group.invoices.push(row);
    });
    return Array.from(groups.values()).map((group) => withTotals(group));
};

const normalizeChallans = (list) => {
    if (!Array.isArray(list) || !list.length) return [];
    const nested = list.some((row) => Array.isArray(row?.invoices));
    if (!nested) return groupFlatInvoices(list);
    return list.map((row, index) => withTotals(toChallan(
        {
            ...row,
            challan_id: row?.challan_id || row?.gst_challan_id || row?.id,
        },
        row.invoices || [],
        index
    ), true));
};

const headCellSx = {
    fontWeight: 600,
    fontSize: 13,
    color: '#28326f',
    backgroundColor: '#f4f6fb',
    whiteSpace: 'nowrap',
};

const expandColSx = {
    ...headCellSx,
    width: 40,
    minWidth: 40,
    maxWidth: 40,
    px: 0.5,
    textAlign: 'center',
};

const actionColSx = {
    ...headCellSx,
    width: 70,
    minWidth: 70,
    maxWidth: 70,
    px: 0.5,
    textAlign: 'center',
};

const InvoiceTable = ({ invoices }) => (
    <Table size="small" sx={{ backgroundColor: '#fff' }}>
        <TableHead>
            <TableRow>
                {['Beneficiary', 'Voucher', 'Taxable Amount', 'Invoice Amount', 'IGST', 'CGST', 'SGST'].map((label) => (
                    <TableCell key={label} sx={{ ...headCellSx, backgroundColor: '#eef2f8' }}>{label}</TableCell>
                ))}
            </TableRow>
        </TableHead>
        <TableBody>
            {invoices.map((invoice, index) => (
                <TableRow key={`${invoice?.invoice_id || invoice?.id || index}`}>
                    <TableCell>
                        <div style={{ fontWeight: 600 }}>{invoice?.company_name || invoice?.beneficiary_name || '-'}</div>
                        <div className="text-muted" style={{ fontSize: 12 }}>
                            GST: {invoice?.gst_no || '-'}
                        </div>
                    </TableCell>
                    <TableCell>
                        <div style={{ fontWeight: 600 }}>{invoice?.voucher_no || '-'}</div>
                        <div className="text-muted" style={{ fontSize: 12 }}>{formatDate(invoice?.voucher_date)}</div>
                    </TableCell>
                    <TableCell>{formatMoney(invoice?.taxable_amount)}</TableCell>
                    <TableCell>{formatMoney(invoice?.payable_amount || invoice?.invoice_value)}</TableCell>
                    <TableCell>{formatMoney(invoice?.i_gst_amount)}</TableCell>
                    <TableCell>{formatMoney(invoice?.c_gst_amount)}</TableCell>
                    <TableCell>{formatMoney(invoice?.s_gst_amount)}</TableCell>
                </TableRow>
            ))}
        </TableBody>
    </Table>
);

const GSTReportList = ({ data, amountTick = 0 }) => {
    const challans = useMemo(() => normalizeChallans(data), [data]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [openKey, setOpenKey] = useState('');

    useEffect(() => {
        setPage(0);
        setOpenKey('');
    }, [data]);

    const pageRows = challans.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const openChallan = (challan) => {
        const ref = challanDocumentRef(challan);
        if (!ref) {
            toast.error('Document unavailable', {
                position: toast.POSITION.TOP_CENTER,
            });
            return;
        }
        openSecureDocumentOrToast(ref, {
            fallbackFilename: `gst_challan_${challan.challan_no || challan.challan_id || 'document'}`,
        });
    };

    return (
        <Paper sx={{ width: '100%', overflow: 'hidden' }} data-amount-tick={amountTick}>
            <TableContainer sx={{ maxHeight: '72vh' }}>
                <Table stickyHeader size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                    <colgroup>
                        <col style={{ width: 40 }} />
                        <col style={{ width: 70 }} />
                        <col />
                        <col />
                        <col style={{ width: 110 }} />
                        <col style={{ width: 110 }} />
                        <col style={{ width: 110 }} />
                        <col style={{ width: 130 }} />
                        <col style={{ width: 80 }} />
                    </colgroup>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={expandColSx} />
                            <TableCell sx={actionColSx}>Actions</TableCell>
                            <TableCell sx={headCellSx}>Challan No / Date</TableCell>
                            <TableCell sx={headCellSx}>Cheque No / Date</TableCell>
                            <TableCell sx={headCellSx}>IGST</TableCell>
                            <TableCell sx={headCellSx}>CGST</TableCell>
                            <TableCell sx={headCellSx}>SGST</TableCell>
                            <TableCell sx={headCellSx}>Challan Value</TableCell>
                            <TableCell sx={headCellSx}>Invoices</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {!pageRows.length && (
                            <TableRow>
                                <TableCell colSpan={9} align="center" sx={{ py: 4, color: '#6b7280' }}>
                                    No challans found
                                </TableCell>
                            </TableRow>
                        )}
                        {pageRows.map((challan) => {
                            const expanded = openKey === challan.key;
                            const canDownload = hasChallanAttachment(challan);
                            return (
                                <React.Fragment key={challan.key}>
                                    <TableRow hover>
                                        <TableCell sx={expandColSx}>
                                            <IconButton
                                                size="small"
                                                aria-label={expanded ? 'Collapse invoices' : 'Expand invoices'}
                                                onClick={() => setOpenKey(expanded ? '' : challan.key)}
                                            >
                                                {expanded
                                                    ? <KeyboardArrowDownIcon fontSize="small" />
                                                    : <KeyboardArrowRightIcon fontSize="small" />}
                                            </IconButton>
                                        </TableCell>
                                        <TableCell sx={actionColSx}>
                                            {canDownload && (
                                                <Tooltip title="Download challan" arrow>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => openChallan(challan)}
                                                        sx={{
                                                            width: 28,
                                                            height: 28,
                                                            border: '1px solid #1976d2',
                                                            color: '#1976d2',
                                                            borderRadius: '6px',
                                                        }}
                                                    >
                                                        <AiOutlineVerticalAlignBottom size={16} />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <div style={{ fontWeight: 600 }}>{challan.challan_no || '-'}</div>
                                            <div className="text-muted" style={{ fontSize: 12 }}>{formatDate(challan.challan_date)}</div>
                                        </TableCell>
                                        <TableCell>
                                            <div style={{ fontWeight: 600 }}>{challan.cheque_no || '-'}</div>
                                            <div className="text-muted" style={{ fontSize: 12 }}>{formatDate(challan.cheque_date)}</div>
                                        </TableCell>
                                        <TableCell>{formatMoney(challan.i_gst_amount)}</TableCell>
                                        <TableCell>{formatMoney(challan.c_gst_amount)}</TableCell>
                                        <TableCell>{formatMoney(challan.s_gst_amount)}</TableCell>
                                        <TableCell style={{ fontWeight: 600 }}>{formatMoney(challan.challan_value)}</TableCell>
                                        <TableCell>{challan.invoices.length}</TableCell>
                                    </TableRow>
                                    {expanded && (
                                        <TableRow>
                                            <TableCell colSpan={9} sx={{ backgroundColor: '#f8fafc', p: 1.5 }}>
                                                <InvoiceTable invoices={challan.invoices} />
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={challans.length}
                page={page}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
                onPageChange={(_, nextPage) => {
                    setPage(nextPage);
                    setOpenKey('');
                }}
                onRowsPerPageChange={(event) => {
                    setRowsPerPage(Number(event.target.value));
                    setPage(0);
                    setOpenKey('');
                }}
            />
        </Paper>
    );
};

export default GSTReportList;
